import clsx from "clsx";
import {
  AlertCircle,
  ChevronDown,
  ExternalLink,
  GitPullRequest,
  MessageSquareText,
  RefreshCw,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { useGitHubReviewRun, useGitHubReviewRuns } from "@/hooks/useGitHubReviews";
import type { GitHubReviewFinding, GitHubReviewRun, GitHubReviewRunStatus } from "@/lib/types";

import { PageViewHeader } from "../PageViewHeader";
import { ReviewSectionTabs } from "./ReviewSectionTabs";

type ReviewTab = "all" | "pending" | "posted" | "failed";

const tabs: ReviewTab[] = ["all", "pending", "posted", "failed"];

export function GitHubReviewsView(): React.JSX.Element {
  const [tab, setTab] = useState<ReviewTab>("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading, isFetching, error, refetch } = useGitHubReviewRuns();
  const runs = data?.runs ?? [];
  const visibleRuns = useMemo(
    () =>
      runs.filter((run) => {
        if (tab === "pending") {
          return (
            run.status === "queued" ||
            run.status === "running" ||
            run.evaluationRun?.status === "queued" ||
            run.evaluationRun?.status === "running"
          );
        }
        if (tab === "failed") {
          return run.status === "failed" || run.evaluationRun?.status === "failed";
        }
        if (tab === "posted") return run.status === "posted";
        return true;
      }),
    [runs, tab],
  );
  const pendingCount = runs.filter(
    (run) =>
      run.status === "queued" ||
      run.status === "running" ||
      run.evaluationRun?.status === "queued" ||
      run.evaluationRun?.status === "running",
  ).length;

  useEffect(() => {
    if (expandedId && !visibleRuns.some((run) => run.id === expandedId)) {
      setExpandedId(null);
    }
  }, [expandedId, visibleRuns]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PageViewHeader
        title="PR Reviews"
        badge={
          pendingCount > 0 ? (
            <span className="border-cyber-yellow/30 bg-cyber-yellow/10 text-cyber-yellow rounded-full border px-1.5 py-0.5 text-[10px]">
              {pendingCount} pending
            </span>
          ) : (
            <span className="bg-cyber-surface text-cyber-muted rounded-full px-1.5 py-0.5 text-[10px]">
              {data?.total ?? 0} runs
            </span>
          )
        }
        actions={
          <>
            <ReviewSectionTabs active="runs" />
            <div className="border-cyber-border flex items-center overflow-hidden rounded border">
              {tabs.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setTab(value)}
                  className={clsx(
                    "px-2 py-1 text-xs capitalize transition-colors",
                    tab === value
                      ? "bg-cyber-cyan/10 text-cyber-cyan"
                      : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
                  )}
                >
                  {value}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-cyan rounded p-1.5 transition-colors"
              title="Refresh PR reviews"
            >
              <RefreshCw size={14} className={isFetching ? "animate-spin" : ""} />
            </button>
          </>
        }
      />

      <div className="flex-1 overflow-y-auto p-3 md:p-4">
        <div className="mx-auto max-w-5xl space-y-2">
          {isLoading ? (
            <LoadingState />
          ) : error ? (
            <ErrorState
              message={error instanceof Error ? error.message : "Unable to load reviews"}
            />
          ) : visibleRuns.length === 0 ? (
            <EmptyState tab={tab} />
          ) : (
            visibleRuns.map((run) => (
              <ReviewRunCard
                key={run.id}
                run={run}
                expanded={expandedId === run.id}
                onToggle={() => setExpandedId(expandedId === run.id ? null : run.id)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ReviewRunCard({
  run,
  expanded,
  onToggle,
}: {
  run: GitHubReviewRun;
  expanded: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const { data: detail, isLoading, error } = useGitHubReviewRun(expanded ? run.id : null);

  return (
    <article
      className={clsx(
        "border-cyber-border hover:border-cyber-cyan/30 overflow-hidden rounded-lg border transition-colors",
        expanded && "border-cyber-cyan/30",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className={clsx(
          "group block w-full px-3 py-3 text-left transition-colors md:px-4",
          expanded && "bg-cyber-surface/30",
        )}
        aria-expanded={expanded}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-cyber-text truncate text-sm font-medium">
                {run.owner}/{run.repo} #{run.prNumber}
              </span>
              <ReviewStatusBadge status={run.status} />
              {run.evaluationRun && <EvaluationStatusBadge status={run.evaluationRun.status} />}
            </div>
            <div className="text-cyber-muted/75 mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
              <span>{formatRelativeTime(run.createdAt)}</span>
              <span className="inline-flex items-center gap-1">
                <MessageSquareText size={12} />
                {run.findingCount} {run.findingCount === 1 ? "comment" : "comments"}
              </span>
              {run.requesterLogin && <span>Requested by @{run.requesterLogin}</span>}
              <span className="font-mono">{run.headSha.slice(0, 7)}</span>
            </div>
            {run.errorMessage && (
              <div className="mt-2 line-clamp-2 text-xs text-red-400">{run.errorMessage}</div>
            )}
          </div>
          <ChevronDown
            size={16}
            className={clsx(
              "text-cyber-muted group-hover:text-cyber-cyan mt-0.5 shrink-0 transition-transform",
              expanded && "rotate-180",
            )}
          />
        </div>
      </button>

      {expanded && (
        <div className="border-cyber-border bg-cyber-bg/50 border-t px-3 py-4 md:px-4">
          {isLoading || !detail ? (
            error ? (
              <ErrorState
                message={error instanceof Error ? error.message : "Unable to load review"}
              />
            ) : (
              <div className="text-cyber-muted py-4 text-sm">Loading review details...</div>
            )
          ) : (
            <ReviewRunDetail run={detail.run} findings={detail.findings} />
          )}
        </div>
      )}
    </article>
  );
}

function ReviewRunDetail({
  run,
  findings,
}: {
  run: GitHubReviewRun;
  findings: GitHubReviewFinding[];
}): React.JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ExternalLinkButton href={run.prUrl}>Open pull request</ExternalLinkButton>
        {run.postedReviewUrl && (
          <ExternalLinkButton href={run.postedReviewUrl}>Open posted review</ExternalLinkButton>
        )}
        <span className="text-cyber-muted ml-auto text-[11px]">
          Run <span className="font-mono">{run.id}</span>
        </span>
      </div>

      {run.errorMessage && (
        <div className="flex gap-2 rounded border border-red-500/20 bg-red-500/5 p-3 text-sm text-red-400">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{run.errorMessage}</span>
        </div>
      )}

      {run.evaluationRun?.errorMessage && (
        <div className="flex gap-2 rounded border border-red-500/20 bg-red-500/5 p-3 text-sm text-red-400">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>Evaluation failed: {run.evaluationRun.errorMessage}</span>
        </div>
      )}

      {findings.length === 0 ? (
        <div className="border-cyber-border bg-cyber-surface/20 text-cyber-muted rounded border border-dashed px-4 py-5 text-center text-sm">
          {run.status === "queued" || run.status === "running"
            ? "Review is still running. Comments will appear here when posted."
            : "No review comments were recorded for this run."}
        </div>
      ) : (
        <section>
          <div className="text-cyber-muted mb-2 text-[10px] tracking-wider uppercase">
            Review comments
          </div>
          <div className="space-y-2">
            {findings.map((finding) => (
              <FindingCard key={finding.id} finding={finding} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function FindingCard({ finding }: { finding: GitHubReviewFinding }): React.JSX.Element {
  return (
    <div className="border-cyber-border bg-cyber-surface/30 rounded border">
      <div className="border-cyber-border flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <SeverityBadge severity={finding.severity} />
        <span className="text-cyber-text min-w-0 truncate font-mono text-xs">
          {finding.path}:{finding.line}
        </span>
        {finding.placement === "summary" && (
          <span className="bg-cyber-surface text-cyber-muted rounded px-1.5 py-0.5 text-[10px] tracking-wider uppercase">
            Summary
          </span>
        )}
        {finding.evaluation && (
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <EvaluationOutcomeBadge label={finding.evaluation.validity} />
            <EvaluationOutcomeBadge label={finding.evaluation.finalState} />
          </div>
        )}
      </div>
      <div className="prose prose-sm prose-invert text-cyber-text [&_a]:text-cyber-cyan [&_code]:text-cyber-magenta [&_pre]:border-cyber-border [&_pre]:bg-cyber-bg max-w-none px-3 py-2.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_pre]:border">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{finding.bodyMarkdown}</ReactMarkdown>
      </div>
      {finding.evaluation && (
        <div className="border-cyber-border bg-cyber-bg/40 border-t px-3 py-2.5">
          <div className="text-cyber-muted mb-1.5 flex flex-wrap items-center gap-2 text-[10px] tracking-wider uppercase">
            <span>Terminal evaluation</span>
            <span>Thread {finding.evaluation.threadState}</span>
          </div>
          <div className="prose prose-sm prose-invert text-cyber-muted max-w-none [&_p:first-child]:mt-0 [&_p:last-child]:mb-0">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {finding.evaluation.rationaleMarkdown}
            </ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
}

function EvaluationStatusBadge({
  status,
}: {
  status: NonNullable<GitHubReviewRun["evaluationRun"]>["status"];
}): React.JSX.Element {
  const tone =
    status === "completed"
      ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-400"
      : status === "failed"
        ? "border-red-400/30 bg-red-400/10 text-red-400"
        : "border-cyber-magenta/30 bg-cyber-magenta/10 text-cyber-magenta";
  return (
    <span
      className={clsx("rounded border px-1.5 py-0.5 text-[10px] tracking-wider uppercase", tone)}
    >
      Evaluation {status}
    </span>
  );
}

function EvaluationOutcomeBadge({ label }: { label: string }): React.JSX.Element {
  const positive = label === "valid" || label === "not_reproducible";
  const negative = label === "invalid" || label === "reproducible";
  return (
    <span
      className={clsx(
        "rounded border px-1.5 py-0.5 text-[10px] tracking-wider uppercase",
        positive && "border-emerald-400/30 bg-emerald-400/10 text-emerald-400",
        negative && "border-red-400/30 bg-red-400/10 text-red-400",
        !positive && !negative && "border-cyber-border bg-cyber-surface text-cyber-muted",
      )}
    >
      {label.replace(/_/g, " ")}
    </span>
  );
}

function ReviewStatusBadge({ status }: { status: GitHubReviewRunStatus }): React.JSX.Element {
  const label = status === "queued" || status === "running" ? "pending" : status;
  return (
    <span
      className={clsx(
        "rounded border px-1.5 py-0.5 text-[10px] tracking-wider uppercase",
        getStatusTone(status),
      )}
    >
      {label}
    </span>
  );
}

function SeverityBadge({
  severity,
}: {
  severity: GitHubReviewFinding["severity"];
}): React.JSX.Element {
  const tone = {
    high: "border-red-400/30 bg-red-400/10 text-red-400",
    medium: "border-cyber-yellow/30 bg-cyber-yellow/10 text-cyber-yellow",
    low: "border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan",
    nit: "border-cyber-border bg-cyber-surface text-cyber-muted",
  }[severity];
  return (
    <span
      className={clsx("rounded border px-1.5 py-0.5 text-[10px] tracking-wider uppercase", tone)}
    >
      {severity}
    </span>
  );
}

function ExternalLinkButton({
  href,
  children,
}: {
  href: string;
  children: string;
}): React.JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="border-cyber-border bg-cyber-surface/40 text-cyber-muted hover:border-cyber-cyan/30 hover:text-cyber-cyan inline-flex items-center gap-1 rounded border px-2 py-1 text-xs transition-colors"
    >
      {children}
      <ExternalLink size={12} />
    </a>
  );
}

function LoadingState(): React.JSX.Element {
  return (
    <div className="text-cyber-muted py-8 text-center">
      <span className="animate-spin-glow mr-2 inline-block">&#9680;</span>
      Loading PR reviews...
    </div>
  );
}

function ErrorState({ message }: { message: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-center gap-2 py-8 text-sm text-red-400">
      <AlertCircle size={16} />
      {message}
    </div>
  );
}

function EmptyState({ tab }: { tab: ReviewTab }): React.JSX.Element {
  return (
    <div className="py-16 text-center">
      <GitPullRequest size={32} className="text-cyber-muted/30 mx-auto mb-3" />
      <p className="text-cyber-muted mb-1 text-sm">
        {tab === "all" ? "No PR reviews yet" : `No ${tab} PR reviews`}
      </p>
      <p className="text-cyber-muted/60 text-xs">
        Reviews appear after Sherlog receives a GitHub review request.
      </p>
    </div>
  );
}

function getStatusTone(status: GitHubReviewRunStatus): string {
  switch (status) {
    case "queued":
    case "running":
      return "border-cyber-yellow/30 bg-cyber-yellow/10 text-cyber-yellow";
    case "posted":
      return "border-emerald-400/30 bg-emerald-400/10 text-emerald-400";
    case "failed":
      return "border-red-400/30 bg-red-400/10 text-red-400";
    case "superseded":
      return "border-cyber-border bg-cyber-surface text-cyber-muted";
  }
}

function formatRelativeTime(value: string): string {
  const elapsedSeconds = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 1_000));
  if (elapsedSeconds < 60) return "just now";
  const elapsedMinutes = Math.floor(elapsedSeconds / 60);
  if (elapsedMinutes < 60) return `${elapsedMinutes}m ago`;
  const elapsedHours = Math.floor(elapsedMinutes / 60);
  if (elapsedHours < 24) return `${elapsedHours}h ago`;
  const elapsedDays = Math.floor(elapsedHours / 24);
  if (elapsedDays < 7) return `${elapsedDays}d ago`;
  return new Date(value).toLocaleDateString();
}
